import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { User } from "@supabase/supabase-js";
import { supabase } from "./supabase";
import { analyticsService } from './services/analytics-service';
import { cookieService } from './services/cookie-service';
import { userService } from "./supabase-service";

// Get Supabase URL from environment
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://supamozo.live';
// Mock data available if needed
// import { getMockUsers } from './mockDataService';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  initialized: boolean;
  signIn: (emailOrPhone: string, password: string, rememberMe: boolean) => Promise<User | null>;
  signUp: (emailOrPhone: string, password: string, username: string, fullName: string, birthday: string, avatarUrl: string | null, gdprSettings?: any, locationData?: any) => Promise<User | null>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  clearError: () => void;
}

const defaultContext: AuthContextType = {
  user: null,
  loading: true,
  initialized: false,
  signIn: async () => null,
  signUp: async () => null,
  signOut: async () => {},
  refreshSession: async () => {},
  clearError: () => {},
};

export const AuthContext = createContext<AuthContextType>(defaultContext);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

/****
 * Provides authentication context and state management for child components using Supabase.
 *
 * Handles user session initialization with retry logic, sign-in, sign-up with profile creation, sign-out, and error state management. Ensures user profiles are created when necessary and exposes authentication functions and state to descendants.
 *
 * @param children - React nodes that will have access to the authentication context.
 *
 * @remark Returns `null` and renders nothing until authentication initialization is complete.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<{
    user: User | null;
    loading: boolean;
    initialized: boolean;
    error: string | null;
  }>({
    user: null,
    loading: true,
    initialized: false,
    error: null,
  });

  useEffect(() => {
    console.log("[AuthProvider] ▶ mounting auth effect");

    let mounted = true;
    let initializationAttempts = 0;
    const maxAttempts = 3;

    // Function to initialize auth with retry logic
    const initializeAuth = async () => {
      try {
        // First check if we have a session
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error("[AuthProvider] Session error:", sessionError);
          if (initializationAttempts < maxAttempts) {
            initializationAttempts++;
            console.log(`[AuthProvider] Retrying initialization (${initializationAttempts}/${maxAttempts})`);
            setTimeout(initializeAuth, 1000); // Retry after 1 second
            return;
          }
        }

        if (!mounted) return;
        
        // If we have a session, try to get the user
        if (sessionData.session) {
          console.log("[AuthProvider] Session found, getting user");
          const { data: userData, error: userError } = await supabase.auth.getUser();
          
          if (userError) {
            console.error("[AuthProvider] Error getting user:", userError);
            // Try to refresh the session
            const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
            
            if (refreshError) {
              console.error("[AuthProvider] Session refresh failed:", refreshError);
              setState((prev) => ({
                ...prev,
                user: null,
                loading: false,
                initialized: true,
                error: refreshError.message,
              }));
              return;
            }
            
            // Use the refreshed user data
            setState((prev) => ({
              ...prev,
              user: refreshData.user,
              loading: false,
              initialized: true,
              error: null,
            }));
            
            if (refreshData.user) {
              userService
                .createMissingProfiles()
                .catch((e) => console.warn("Profile creation error:", e));
            }
          } else {
            // User data retrieved successfully
            setState((prev) => ({
              ...prev,
              user: userData.user,
              loading: false,
              initialized: true,
              error: null,
            }));
            
            if (userData.user) {
              userService
                .createMissingProfiles()
                .catch((e) => console.warn("Profile creation error:", e));
            }
          }
        } else {
          // No session found
          setState((prev) => ({
            ...prev,
            user: null,
            loading: false,
            initialized: true,
            error: null,
          }));
        }
      } catch (error) {
        console.error("[AuthProvider] Initialization error:", error);
        if (!mounted) return;
        
        setState((prev) => ({
          ...prev,
          loading: false,
          initialized: true,
          error: error instanceof Error ? error.message : "Unknown error during authentication",
        }));
      }
    };

    // Start initialization
    initializeAuth();
    
    // Subscribe to auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;

      console.log("[AuthProvider] onAuthStateChange:", event, session);
      setState((prev) => ({
        ...prev,
        user: session?.user ?? null,
        loading: false,
        initialized: true,
        error: null,
      }));

      if (event === "SIGNED_IN" && session?.user) {
        console.log("[AuthProvider] user signed in:", session.user.email);
        userService
          .createMissingProfiles()
          .catch((e) => console.warn("Profile creation error:", e));
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // COMPLETELY DISABLED: Auto logout on inactivity and cookie clearing detection
  useEffect(() => {
    if (!state.user) return; // Only run when user is logged in
    
    console.log("[AuthProvider] Auto logout and cookie detection completely disabled");
    
    // Add minimal session monitoring to detect any unexpected changes
    const storageKey = `sb-${supabaseUrl.split("//")[1].split(".")[0]}-auth-token`;
    const checkInterval = setInterval(() => {
      const authToken = localStorage.getItem(storageKey);
      console.log("[AuthProvider] Session monitor check:", {
        hasAuthToken: !!authToken,
        currentTime: new Date().toISOString()
      });
    }, 60000); // Check every minute for debugging purposes only
    
    return () => {
      clearInterval(checkInterval);
    };
  }, [state.user]); // Re-run when user state changes

  const signIn = async (emailOrPhone: string, password: string, rememberMe: boolean = false) => {
    try {
      console.log("Starting sign in process...");
      
      // Import the auth service
      const { default: authService } = await import('./services/auth-service');
      
      // Use the universal auth service to login the user
      const user = await authService.loginUserUniversal(emailOrPhone, password);
      
      console.log("User signed in successfully");
      
      // Update state with the signed-in user
      if (user) {
        setState((prev) => ({
          ...prev,
          user,
        }));

        // Set auth cookies
        cookieService.setAuthCookies(emailOrPhone, rememberMe);

        // Track login analytics
        const loginMethod = emailOrPhone.includes('@') ? 'email' : 'phone';
        analyticsService.trackLogin(user.id, loginMethod, { rememberMe });
      }
      
      // Return the user
      return user;
    } catch (error) {
      console.error("Error during sign in:", error);
      throw error;
    }
  };

  const signUp = async (
    emailOrPhone: string,
    password: string,
    username: string,
    fullName: string,
    birthday: string,
    avatarUrl: string | null,
    gdprSettings?: any,
    locationData?: any
  ) => {
    try {
      console.log("Starting user registration process...");
      
      // Import the auth service
      const { default: authService } = await import('./services/auth-service');
      
      // Use the universal auth service to register the user
      const newUser = await authService.registerUserUniversal(
        emailOrPhone,
        password,
        username,
        fullName,
        birthday,
        avatarUrl,
        gdprSettings,
        locationData
      );
      
      console.log("User registered successfully");
      
      // Update state with the new user
      if (newUser) {
        setState((prev) => ({
          ...prev,
          user: newUser,
        }));

        // Track signup analytics
        const signupMethod = emailOrPhone.includes('@') ? 'email' : 'phone';
        analyticsService.trackSignup(newUser.id, signupMethod, {
          hasGdprConsent: gdprSettings?.gdprAccepted,
          hasLocationConsent: gdprSettings?.locationConsent,
          locationData: locationData
        });
      }
      
      // Return the created user
      return newUser;
    } catch (error) {
      console.error("Error during signup:", error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      // Track logout in background (non-blocking)
      if (state.user) {
        try {
          analyticsService.trackLogout(state.user.id);
        } catch (error) {
          console.warn('Analytics logout tracking failed:', error);
        }
      }

      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      setState({
        user: null,
        loading: false,
        initialized: true,
        error: null
      });

      console.log("User signed out successfully");
    } catch (error) {
      console.error("Error signing out:", error);
      throw error;
    }
  };

  const clearError = useCallback(() => {
    setState((prev) => ({
      ...prev,
      error: null
    }));
  }, []);

  const refreshSession = async () => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      
      // First get the current session
      const { data: sessionData } = await supabase.auth.getSession();

      if (!sessionData.session) {
        console.log("No active session found, attempting to recover");
        
        // Try to recover by checking local storage for auth tokens
        const storageKey = `sb-${supabaseUrl.split("//")[1].split(".")[0]}-auth-token`;
        const hasLocalToken = localStorage.getItem(storageKey);
        
        if (hasLocalToken) {
          console.log("Found local token, attempting to restore session");
          // Try to get a new session using the token in localStorage
          const { data: newSession, error: newSessionError } = await supabase.auth.getSession();
          
          if (newSessionError) {
            console.error("Failed to restore session from local token:", newSessionError);
            setState((prev) => ({ ...prev, loading: false, error: "Session expired. Please sign in again." }));
            return;
          }
          
          if (newSession.session) {
            console.log("Successfully restored session from local token");
            setState((prev) => ({
              ...prev,
              user: newSession.session?.user ?? null,
              loading: false,
              error: null,
            }));
            return;
          }
        }
        
        // If we get here, we couldn't recover the session
        setState((prev) => ({ 
          ...prev, 
          user: null,
          loading: false, 
          error: "Your session has expired. Please sign in again." 
        }));
        return;
      }

      // We have a session, try to refresh it
      console.log("Active session found, refreshing");
      const { data, error } = await supabase.auth.refreshSession();

      if (error) {
        console.error("Error refreshing session:", error);
        
        // Check if it's an auth session missing error
        if (error.message?.includes('Auth session missing')) {
          console.log("Auth session missing, trying to recover");
          
          // Try to get a new session
          const { data: newSession, error: newSessionError } = await supabase.auth.getSession();
          
          if (newSessionError) {
            console.error("Failed to get new session:", newSessionError);
            setState((prev) => ({ ...prev, loading: false, error: "Session expired. Please sign in again." }));
            return;
          }
          
          if (newSession.session) {
            console.log("Successfully recovered session");
            setState((prev) => ({
              ...prev,
              user: newSession.session?.user ?? null,
              loading: false,
              error: null,
            }));
            return;
          }
        }
        
        // If we get here, we couldn't refresh or recover the session
        setState((prev) => ({ 
          ...prev, 
          loading: false, 
          error: "Failed to refresh your session. Please sign in again." 
        }));
        return;
      }

      if (data?.session) {
        console.log("Session refreshed successfully");
        setState((prev) => ({
          ...prev,
          user: data.session?.user ?? null,
          loading: false,
          error: null,
        }));
      } else {
        console.warn("Session refresh did not return a session");
        setState((prev) => ({ 
          ...prev, 
          user: null,
          loading: false, 
          error: "Your session has expired. Please sign in again." 
        }));
      }
    } catch (error) {
      console.error("Failed to refresh session:", error);
      setState((prev) => ({ 
        ...prev, 
        loading: false, 
        error: error instanceof Error ? error.message : "An unknown error occurred" 
      }));
    }
  };

  const contextValue: AuthContextType = {
    user: state.user,
    loading: state.loading,
    initialized: state.initialized,
    signIn,
    signUp,
    signOut,
    refreshSession,
    clearError,
  };

  if (!state.initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin h-10 w-10 border-4 border-green-900 border-t-transparent rounded-full dark:border-yellow-500"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}
